import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../store/user/user.selector';
import { useShow } from '../../../hooks/useShow';
import { markShowCardListed } from '../../../utils/firebase/ebayToSales';

import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
    IconDeviceTv,
    IconArrowLeft,
    IconArrowRight,
    IconCircleCheck,
    IconCircle,
    IconCurrencyDollar,
    IconNotes,
    IconList,
    IconFileExport,
    IconGripVertical,
} from '@tabler/icons-react';

import Title from '../../../components/widgets/components/title/Title';
import InputBar from '../../../components/input-bar/InputBar';
import CardAttribute from '../../../components/card-attribute/CardAttribute';
import PrimaryBtn from '../../../components/btns/primary-btn/PrimaryBtn';
import {
    HostContainer,
    TopBar,
    LaunchBtn,
    NavStrip,
    NavBtn,
    CardPosition,
    MainLayout,
    ActiveCardPanel,
    ActiveCardName,
    ActiveCardMeta,
    ActiveCardParallel,
    ControlsPanel,
    ControlSection,
    ControlLabel,
    SoldToggle,
    PriceInput,
    NotesInput,
    RunlistSection,
    RunlistItem,
    RunlistSwatch,
    RunlistInfo,
    RunlistName,
    RunlistIndex,
    RunlistSoldDot,
    DragHandle,
    EmptyState,
    ShowConfigPanel,
    ShowConfigFields,
    ShowConfigActions,
    ExportBtn,
} from './ShowHost.styles';

/* ─── CSV helpers ─────────────────────────────────────────────────────────── */

const CSV_HEADERS = [
    'Category', 'Sub Category', 'Title', 'Description',
    'Quantity', 'Type', 'Price', 'Shipping Profile',
    'Offerable', 'Hazmat', 'Condition', 'Cost Per Item', 'SKU',
    'Image URL 1', 'Image URL 2', 'Image URL 3', 'Image URL 4',
    'Image URL 5', 'Image URL 6', 'Image URL 7', 'Image URL 8',
];

function csvEscape(value) {
    const str = value == null ? '' : String(value);
    return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
}

function buildCsvTitle(item) {
    const name     = Array.isArray(item.name) ? item.name.join(' & ') : (item.name ?? '');
    const parallel = item.cardParallelName ?? 'Base';
    const num      = item.cardNumber ? `#${item.cardNumber} ` : '';
    if (item.cardPrintNumber != null && item.cardSupply != null)
        return `${num}${name} ${parallel} #${item.cardPrintNumber}/${item.cardSupply}`;
    if (item.cardSupply != null)
        return `${num}${name} ${parallel} /${item.cardSupply}`;
    return `${num}${name} ${parallel}`;
}

function buildCsvDescription(item) {
    const set = [item.year, item.manufacturer, item.series].filter(Boolean).join(' ');
    return set ? `${set}; Ungraded near mint or better` : 'Ungraded near mint or better';
}

function generateCsv(items, category, subCategory, shippingProfile) {
    const rows = [CSV_HEADERS.map(csvEscape).join(',')];
    items.forEach((item) => {
        const row = [
            category, subCategory,
            buildCsvTitle(item), buildCsvDescription(item),
            1, 'Auction', 1, shippingProfile,
            '', 'Not Hazmat', 'Raw - Near Mint or Better', '', '',
            '', '', '', '', '', '', '', '',
        ];
        rows.push(row.map(csvEscape).join(','));
    });
    return rows.join('\n');
}

function downloadCsv(csv, filename) {
    const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = Object.assign(document.createElement('a'), { href: url, download: filename });
    link.click();
    URL.revokeObjectURL(url);
}

/* ─── Display helpers ─────────────────────────────────────────────────────── */

function buildParallelLabel(card) {
    const name = card.cardParallelName ?? 'BASE';
    if (card.cardPrintNumber != null && card.cardSupply != null)
        return `${name} #${card.cardPrintNumber}/${card.cardSupply}`;
    if (card.cardSupply != null) return `${name} /${card.cardSupply}`;
    return name;
}

function buildMetaLine(card) {
    return [card.series].filter(Boolean).join(' · ');
}

const CHANNEL_NAME = (id) => `whatnot-show-${id}`;

/* ─── Sortable runlist item ───────────────────────────────────────────────── */

function SortableRunlistItem({ card, index, cardIndex, total, onGoTo, onRemove }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: card.id });

    const name = Array.isArray(card.name) ? card.name.join(' & ') : (card.name ?? '—');

    return (
        <RunlistItem
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            $active={index === cardIndex}
            $dragging={isDragging}
            onClick={() => !isDragging && onGoTo(index)}
        >
            <DragHandle {...listeners} {...attributes} onClick={(e) => e.stopPropagation()}>
                <IconGripVertical size={14} />
            </DragHandle>
            <RunlistIndex>{index + 1} of {total}</RunlistIndex>
            <RunlistSwatch $color={card.cardColor ?? '#fff'} />
            <RunlistInfo>
                <RunlistName>{name}</RunlistName>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {card.series && <CardAttribute text={card.series} size="S" />}
                    {card.cardNumber && <CardAttribute text={`#${card.cardNumber}`} size="S" />}
                    <CardAttribute text={buildParallelLabel(card)} color={card.cardColor ?? null} size="S" />
                </div>
            </RunlistInfo>
            {card.sold && <RunlistSoldDot />}
            <CardAttribute
                text="REMOVE"
                color="#ef4444"
                onClick={(e) => { e.stopPropagation(); onRemove(card.id); }}
            />
        </RunlistItem>
    );
}

/* ─── Component ───────────────────────────────────────────────────────────── */

export default function ShowHost() {
    const { showId } = useParams();
    const navigate   = useNavigate();
    const uid        = useSelector(selectUser)?.uid;
    const { show, cards, isLoading, setCurrentCardIndex, updateCard, updateShowName, updateShow, removeCard, deleteCurrentShow } = useShow(showId);

    const cardIndex = show?.currentCardIndex ?? 0;

    /* ── Sorted cards (local copy for drag-and-drop — syncs from Firestore
          only when the set of card IDs changes, not on order updates) ── */
    const [sortedCards, setSortedCards] = useState([]);
    const prevIdsRef = useRef('');
    useEffect(() => {
        const ids = cards.map((c) => c.id).sort().join(',');
        if (ids !== prevIdsRef.current) {
            prevIdsRef.current = ids;
            setSortedCards(cards);
        }
    }, [cards]);

    const card = (sortedCards[cardIndex] ?? cards[cardIndex]) ?? null;

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setSortedCards((prev) => {
            const oldIdx   = prev.findIndex((c) => c.id === active.id);
            const newIdx   = prev.findIndex((c) => c.id === over.id);
            const reordered = arrayMove(prev, oldIdx, newIdx);

            // Persist new order to Firestore (fire-and-forget)
            reordered.forEach((c, i) => updateCard(c.id, { order: i }));

            return reordered;
        });
    }, [updateCard]);

    /* ── Show-level fields (seeded once from Firestore on load) ── */
    const [showName,        setShowName]        = useState('');
    const [showDate,        setShowDate]        = useState('');
    const [category,        setCategory]        = useState('Sports Cards');
    const [subCategory,     setSubCategory]     = useState('Baseball Singles');
    const [shippingProfile, setShippingProfile] = useState('1-3 oz');

    const initializedRef = useRef(false);
    useEffect(() => { initializedRef.current = false; }, [showId]);
    useEffect(() => {
        if (isLoading || initializedRef.current) return;
        setShowName(show?.name ?? '');
        setShowDate(show?.showDate ?? '');
        setCategory(show?.category ?? 'Sports Cards');
        setSubCategory(show?.subCategory ?? 'Baseball Singles');
        setShippingProfile(show?.shippingProfile ?? '1-3 oz');
        initializedRef.current = true;
    }, [show, isLoading]);

    /* ── Per-card fields (reset when active card changes) ── */
    const [finalBid, setFinalBid] = useState('');
    const [notes,    setNotes]    = useState('');
    const [markingListed, setMarkingListed] = useState(false);
    useEffect(() => {
        setFinalBid(card?.finalBid != null ? String(card.finalBid) : '');
        setNotes(card?.notes ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [card?.id]);

    /* ── BroadcastChannel (host → viewer) ── */
    const channelRef = useRef(null);
    useEffect(() => {
        if (!showId) return;
        channelRef.current = new BroadcastChannel(CHANNEL_NAME(showId));
        return () => channelRef.current?.close();
    }, [showId]);

    const broadcast = useCallback((msg) => channelRef.current?.postMessage(msg), []);

    /* ── Navigation ── */
    const goTo = useCallback(async (idx) => {
        await setCurrentCardIndex(idx);
        broadcast({ type: 'CARD_CHANGE', payload: { cardIndex: idx } });
    }, [setCurrentCardIndex, broadcast]);

    const prev = useCallback(() => goTo(Math.max(0, cardIndex - 1)), [goTo, cardIndex]);
    const next = useCallback(() => goTo(Math.min(sortedCards.length - 1, cardIndex + 1)), [goTo, cardIndex, sortedCards.length]);

    /* ── Card actions ── */
    const toggleSold = useCallback(async () => {
        if (!card) return;
        const newSold = !card.sold;
        await updateCard(card.id, { sold: newSold });
        broadcast({ type: 'CARD_UPDATE', payload: { sold: newSold, finalBid: card.finalBid ?? null } });
    }, [card, updateCard, broadcast]);

    const commitFinalBid = useCallback(async () => {
        if (!card) return;
        const val = finalBid === '' ? null : Number(finalBid);
        await updateCard(card.id, { finalBid: val });
        broadcast({ type: 'CARD_UPDATE', payload: { sold: card.sold ?? false, finalBid: val } });
    }, [card, finalBid, updateCard, broadcast]);

    const handleMarkListed = useCallback(async () => {
        if (!uid || !showId || !card || markingListed) return;
        setMarkingListed(true);
        try {
            const saleId = await markShowCardListed(uid, showId, card);
            if (saleId) navigate(`/transactions/tracker/${saleId}`);
        } finally {
            setMarkingListed(false);
        }
    }, [uid, showId, card, markingListed, navigate]);

    const commitNotes = useCallback(async () => {
        if (!card) return;
        await updateCard(card.id, { notes });
    }, [card, notes, updateCard]);

    /* ── Launch viewer ── */
    const launchViewer = useCallback(() => {
        window.open(
            `/show/${uid}/${showId}/viewer`,
            'whatnot-viewer',
            'width=1920,height=1080,menubar=no,toolbar=no,location=no,status=no'
        );
    }, [uid, showId]);

    /* ── Delete show ── */
    const [deletePending, setDeletePending] = useState(false);

    const handleDeleteShow = useCallback(async () => {
        if (!deletePending) { setDeletePending(true); return; }
        await deleteCurrentShow();
        navigate('/whatnot');
    }, [deletePending, deleteCurrentShow, navigate]);

    /* ── Export ── */
    const handleExport = useCallback(() => {
        const csv  = generateCsv(cards, category, subCategory, shippingProfile);
        const date = new Date().toISOString().slice(0, 10);
        downloadCsv(csv, `show-${showId}-${date}.csv`);
    }, [cards, category, subCategory, shippingProfile, showId]);

    /* ── Render ── */
    const playerName = Array.isArray(card?.name) ? card.name.join(' & ') : (card?.name ?? '—');

    return (
        <HostContainer>

            {/* Top bar */}
            <TopBar>
                <div style={{ alignItems: 'center', display: 'flex', gap: '16px' }}>
                    <NavBtn onClick={() => navigate('/whatnot')}>
                        <IconArrowLeft />
                    </NavBtn>
                    <Title text="SHOW CONTROL" />
                </div>
            </TopBar>

            {/* Show settings + CSV export + launch */}
            <ShowConfigPanel>
                <ShowConfigFields>
                    <InputBar
                        id="show-name" type="text" label="Show Name"
                        value={showName} updateValue={setShowName}
                        pH="e.g. Friday Night Break"
                        onBlur={() => updateShowName(showName)}
                        onKeyDown={(e) => e.key === 'Enter' && updateShowName(showName)}
                    />
                    <InputBar
                        id="show-date" type="datetime-local" label="Date / Time"
                        value={showDate} updateValue={setShowDate}
                        pH=""
                        onBlur={() => updateShow({ showDate })}
                        onKeyDown={(e) => e.key === 'Enter' && updateShow({ showDate })}
                    />
                    <InputBar
                        id="show-category" type="text" label="Category"
                        value={category} updateValue={setCategory}
                        pH="e.g. Sports Cards"
                        onBlur={() => updateShow({ category })}
                        onKeyDown={(e) => e.key === 'Enter' && updateShow({ category })}
                    />
                    <InputBar
                        id="show-subcategory" type="text" label="Sub Category"
                        value={subCategory} updateValue={setSubCategory}
                        pH="e.g. Baseball Singles"
                        onBlur={() => updateShow({ subCategory })}
                        onKeyDown={(e) => e.key === 'Enter' && updateShow({ subCategory })}
                    />
                    <InputBar
                        id="show-shipping" type="text" label="Shipping Profile"
                        value={shippingProfile} updateValue={setShippingProfile}
                        pH="e.g. 1-3 oz"
                        onBlur={() => updateShow({ shippingProfile })}
                        onKeyDown={(e) => e.key === 'Enter' && updateShow({ shippingProfile })}
                    />
                </ShowConfigFields>
                <ShowConfigActions>
                    <PrimaryBtn
                        type="critical"
                        title={deletePending ? 'Confirm Deletion?' : 'Delete Show'}
                        onClick={handleDeleteShow}
                        onBlur={() => setDeletePending(false)}
                    />
                    <ExportBtn onClick={handleExport} disabled={cards.length === 0}>
                        <IconFileExport size={15} />
                        Export CSV
                    </ExportBtn>
                    <LaunchBtn onClick={launchViewer}>
                        <IconDeviceTv size={16} />
                        Launch Display
                    </LaunchBtn>
                </ShowConfigActions>
            </ShowConfigPanel>

            {/* Loading */}
            {isLoading && <EmptyState><p>Loading show...</p></EmptyState>}

            {/* Empty runlist */}
            {!isLoading && cards.length === 0 && (
                <EmptyState>
                    No cards in runlist
                    <p>Go back to the Whatnot queue, select cards, and use "Add to Show".</p>
                </EmptyState>
            )}

            {/* Main show controls */}
            {!isLoading && cards.length > 0 && (
                <>
                    <MainLayout>
                        {/* Active card summary */}
                        <ActiveCardPanel>
                            <ActiveCardName>{playerName}</ActiveCardName>
                            <ActiveCardMeta>{buildMetaLine(card ?? {})}</ActiveCardMeta>
                            {card?.cardNumber && <ActiveCardMeta>#{card.cardNumber}</ActiveCardMeta>}
                            <ActiveCardParallel $color={card?.cardColor}>
                                {buildParallelLabel(card ?? {})}
                            </ActiveCardParallel>
                        </ActiveCardPanel>

                        {/* Card controls */}
                        <ControlsPanel>
                            <ControlSection>
                                <ControlLabel>Status</ControlLabel>
                                <SoldToggle $sold={card?.sold} onClick={toggleSold}>
                                    {card?.sold
                                        ? <><IconCircleCheck size={16} /> Mark as Unsold</>
                                        : <><IconCircle size={16} /> Mark as Sold</>
                                    }
                                </SoldToggle>
                            </ControlSection>

                            {card?.sourceTransactionId && !card.listedSaleId && (
                                <ControlSection>
                                    <PrimaryBtn
                                        title={markingListed ? 'MARKING...' : 'MARK LISTED'}
                                        onClick={handleMarkListed}
                                        disabled={markingListed}
                                    />
                                </ControlSection>
                            )}
                            {card?.listedSaleId && (
                                <ControlSection>
                                    <PrimaryBtn
                                        title="VIEW SALE"
                                        onClick={() => navigate(`/transactions/tracker/${card.listedSaleId}`)}
                                    />
                                </ControlSection>
                            )}

                            {card?.sold && (
                                <ControlSection>
                                    <ControlLabel htmlFor="final-bid">
                                        <IconCurrencyDollar size={12} style={{ display: 'inline', marginRight: 4 }} />
                                        Final Bid
                                    </ControlLabel>
                                    <PriceInput
                                        id="final-bid" type="number" min={0} placeholder="0"
                                        value={finalBid}
                                        onChange={(e) => setFinalBid(e.target.value)}
                                        onBlur={commitFinalBid}
                                        onKeyDown={(e) => e.key === 'Enter' && commitFinalBid()}
                                    />
                                </ControlSection>
                            )}

                            <ControlSection>
                                <ControlLabel htmlFor="card-notes">
                                    <IconNotes size={12} style={{ display: 'inline', marginRight: 4 }} />
                                    Notes (private)
                                </ControlLabel>
                                <NotesInput
                                    id="card-notes"
                                    placeholder="Condition notes, comps, reminders..."
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    onBlur={commitNotes}
                                />
                            </ControlSection>
                        </ControlsPanel>
                    </MainLayout>

                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <NavStrip>
                            <NavBtn onClick={prev} disabled={cardIndex === 0}>
                                <IconArrowLeft /> Prev
                            </NavBtn>
                            <CardPosition>{cardIndex + 1} of {sortedCards.length}</CardPosition>
                            <NavBtn $primary onClick={next} disabled={cardIndex === sortedCards.length - 1}>
                                Next <IconArrowRight />
                            </NavBtn>
                        </NavStrip>
                    </div>

                    {/* Runlist */}
                    <ControlLabel style={{ marginBottom: -8 }}>
                        <IconList size={12} style={{ display: 'inline', marginRight: 4 }} />
                        Runlist — {sortedCards.length} cards
                    </ControlLabel>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={sortedCards.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                            <RunlistSection>
                                {sortedCards.map((c, i) => (
                                    <SortableRunlistItem
                                        key={c.id}
                                        card={c}
                                        index={i}
                                        cardIndex={cardIndex}
                                        total={sortedCards.length}
                                        onGoTo={goTo}
                                        onRemove={removeCard}
                                    />
                                ))}
                            </RunlistSection>
                        </SortableContext>
                    </DndContext>
                </>
            )}

        </HostContainer>
    );
}
